import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { 
  collection, 
  onSnapshot, 
  query, 
  orderBy,
  limit
} from 'firebase/firestore';
import { 
  History, 
  User, 
  Tag, 
  Package, 
  Image as ImageIcon, 
  Settings, 
  Clock,
  PlusCircle,
  PencilLine,
  Trash2,
  Loader2
} from 'lucide-react';

interface ActivityLog {
  id: string;
  userId: string;
  userName: string;
  userRole: string;
  action: 'create' | 'edit' | 'delete';
  module: 'menu' | 'categories' | 'gallery' | 'settings';
  targetName: string;
  timestamp: string;
}

export default function ActivityLogPage() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'activity_logs'), orderBy('timestamp', 'desc'), limit(100));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ActivityLog[];
      setLogs(logsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create': return <PlusCircle size={14} className="text-green-500" />;
      case 'edit': return <PencilLine size={14} className="text-blue-500" />;
      case 'delete': return <Trash2 size={14} className="text-red-500" />;
      default: return null;
    }
  };

  const getModuleIcon = (module: string) => {
    switch (module) {
      case 'menu': return <Package size={14} />;
      case 'categories': return <Tag size={14} />;
      case 'gallery': return <ImageIcon size={14} />;
      case 'settings': return <Settings size={14} />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#F57F25]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_activity_title')}</h1>
        <p className="text-white/50 text-sm">{t('admin_activity_sub')}</p>
      </div>

      <div className="bg-[#111] border border-white/5 rounded-[32px] overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-black/50 border-b border-white/5">
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_activity_user')}</th>
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_activity_action')}</th>
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_activity_module')}</th>
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_activity_target')}</th>
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_activity_datetime')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-white/[0.02] transition-colors group">
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center text-white/20">
                        <User size={16} />
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm">{log.userName}</p>
                        <p className="text-[#F57F25] text-[9px] font-black uppercase tracking-widest">{log.userRole}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-black rounded-full border border-white/5 text-[10px] font-black uppercase tracking-widest text-white/60">
                      {getActionIcon(log.action)}
                      {t(`action_${log.action}`)}
                    </span>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2 text-white/50 text-xs font-bold uppercase tracking-widest">
                      {getModuleIcon(log.module)}
                      {t(`module_${log.module}`)}
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <p className="text-white text-sm font-medium">{log.targetName}</p>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2 text-white/20 text-xs font-medium">
                      <Clock size={14} />
                      {new Date(log.timestamp).toLocaleString()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {logs.length === 0 && (
        <div className="text-center py-20 bg-[#111] rounded-[32px] border border-dashed border-white/10">
          <History size={48} className="text-white/10 mx-auto mb-4" />
          <p className="text-white/20 font-bold uppercase tracking-widest text-sm">{t('admin_activity_empty')}</p>
        </div>
      )}
    </div>
  );
}
